import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  Log,
  RydeProcessTitle,
  RydeUiUnit,
  TAddressedFieldsValueOnCompleted,
  useProcesses,
} from "@ryde/shared";

interface IUseWatchBulkFetchTrackingHistoryProps {
  entity: RydeUiUnit;
  addressedFieldsValueOnCompleted?: TAddressedFieldsValueOnCompleted;
}

export const useWatchBulkFetchTrackingHistory = ({
  entity,
  addressedFieldsValueOnCompleted,
}: IUseWatchBulkFetchTrackingHistoryProps) => {
  const { t } = useTranslation();
  const bulkFetchTrackingHistoryProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkFetchTrackingHistory,
    entity,
  );
  const bulkFetchTrackingHistoryProcess = useRecoilValue(
    activeProcessSF(bulkFetchTrackingHistoryProcessKey),
  );
  const { stopProcessPolling } = useProcesses();

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      Log.info("BulkFetchTrackingHistory process completed", {
        processId: process.id,
        entity,
      });
      addressedFieldsValueOnCompleted?.(process);
    },
    [addressedFieldsValueOnCompleted, entity],
  );

  const onFailed = useCallback(
    (process: IRydeProcess) => {
      Log.error("BulkFetchTrackingHistory process failed", {
        processId: process.id,
        entity,
        errors: process.errors,
      });
    },
    [entity],
  );

  useEffect(() => {
    handleProcess({
      entity,
      process: bulkFetchTrackingHistoryProcess,
      onComplete,
      onFail: onFailed,
      translationFunction: t,
      stopProcessPolling,
      processTitle: RydeProcessTitle.BulkFetchTrackingHistory,
      toastSummaryOnComplete: true,
    });
  }, [
    bulkFetchTrackingHistoryProcess,
    entity,
    onComplete,
    onFailed,
    stopProcessPolling,
    t,
  ]);
};
